/**
 * Check-in page search params utilities
 * 
 * Pure functions for validating and parsing URL search params
 * for the check-in route. Supports pre-populating the date
 * when navigating from daily view or timeline.
 */

/**
 * Search params schema for check-in page
 * - date: Optional YYYY-MM-DD string to pre-populate the form
 */
export interface CheckInSearchParams {
  date?: string;
  edit?: boolean;
}

/**
 * Parse boolean-like search values.
 * Accepts true boolean or "true" string.
 */
function parseBooleanSearchParam(value: unknown): boolean | undefined {
  if (value === true || value === "true") {
    return true;
  }

  if (value === false || value === "false") {
    return false;
  }

  return undefined;
}

/**
 * Validate YYYY-MM-DD date string format
 * 
 * @pure
 */
export function isValidDateFormat(value: unknown): value is string {
  if (typeof value !== "string") return false;
  
  // Must match YYYY-MM-DD pattern
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!datePattern.test(value)) return false;
  
  // Parse and verify it's a real date
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

/**
 * Check if a date is within valid range for pain entry
 * - Not in the future
 * - Not more than 365 days in the past
 * 
 * @pure
 */
export function isDateInValidRange(dateString: string): boolean {
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  const now = new Date(Date.now());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  today.setHours(23, 59, 59, 999);
  
  // Cannot be in future
  if (date > today) return false;
  
  // Cannot be more than 365 days in past
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() - 365);
  minDate.setHours(0, 0, 0, 0);
  
  if (date < minDate) return false;
  
  return true;
}

/**
 * Parse date string to Date object (local timezone)
 * 
 * @pure
 */
export function parseDateString(dateString: string): Date {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Validate search params for check-in page
 * Returns validated params or defaults
 * 
 * This function is designed to work with TanStack Router's
 * validateSearch option which receives unknown search params.
 * 
 * @pure
 */
export function validateCheckInSearchParams(
  search: Record<string, unknown>
): CheckInSearchParams {
  const result: CheckInSearchParams = {};

  // Validate edit mode flag first, since it affects date validation rules
  const edit = parseBooleanSearchParam(search.edit);
  if (edit !== undefined) {
    result.edit = edit;
  }
  
  // Validate date param if present
  if (search.date !== undefined) {
    const dateParam = search.date;

    // In edit mode, allow any valid date so existing records can always be edited.
    // In normal check-in mode, keep the date range constraints.
    const allowDate =
      isValidDateFormat(dateParam) &&
      (result.edit === true || isDateInValidRange(dateParam));

    if (allowDate) {
      result.date = dateParam;
    }
    // Invalid dates are silently ignored (use default)
  }

  return result;
}

/**
 * Get Date object from validated search params
 * Returns today if no date param or param is invalid
 * 
 * @pure
 */
export function getInitialDateFromSearchParams(
  params: CheckInSearchParams
): Date {
  if (params.date && isValidDateFormat(params.date)) {
    return parseDateString(params.date);
  }
  return new Date();
}
